import React from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Field, HStack, sizes } from '@rtkwlf/fenrir-react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { LinkButton } from '@packages/components/link-button';
import {
  Box,
  CircularProgress,
  MenuItem,
  OutlinedInput,
  Select,
} from '@packages/fenrir/components';
import { ACCOUNT_ALERT_CONFIG_RULE_ROUTE } from 'ae-constants/routes.constants';

import type { TicketRuleTypeSelectValue } from './use-ticket-rule-type-select-options/use-ticket-rule-type-select-options';
import { useTicketRuleTypeSelectOptions } from './use-ticket-rule-type-select-options/use-ticket-rule-type-select-options';

interface TicketRuleTypeSelectProps {
  value?: TicketRuleTypeSelectValue;
  onChange: (value: TicketRuleTypeSelectValue) => void;
}

const StyledFieldRoot = styled(Field.Root)({
  maxWidth: 'max-content',
});

export const TicketRuleTypeSelect: React.FC<TicketRuleTypeSelectProps> = ({
  value = '',
  onChange,
}) => {
  const { t } = useTranslation('ticket');
  const { options, isLoading } = useTicketRuleTypeSelectOptions();

  return (
    <HStack width="full" marginY="smedium">
      <StyledFieldRoot isRequired appearance="neutral">
        <Field.Label paddingBottom="zero" htmlFor="rule-type">
          {t('ruleTypeSelect.label')}
        </Field.Label>

        <Select
          labelId="rule-type"
          label={t('ruleTypeSelect.label')}
          value={value}
          onChange={(event) => {
            onChange(event.target.value as TicketRuleTypeSelectValue);
          }}
          disabled={isLoading}
          sx={{ width: sizes['20rem'] }}
          input={<OutlinedInput />}
          IconComponent={
            isLoading
              ? () => (
                  <Box sx={{ display: 'flex', paddingRight: '0.5rem' }}>
                    <CircularProgress size="1.5rem" color="inherit" />
                  </Box>
                )
              : undefined
          }
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.text}
            </MenuItem>
          ))}
        </Select>
      </StyledFieldRoot>

      {value && value !== 'other' && (
        <LinkButton
          variant="link"
          to={generatePath(ACCOUNT_ALERT_CONFIG_RULE_ROUTE, { alertId: value })}
          external
          padding={0}
          css={css({ alignSelf: 'flex-end' })}
        >
          {t('reviewAlertConfigurations')}
        </LinkButton>
      )}
    </HStack>
  );
};
